import type { SYSFeature, SYSForm, SYSFormValidationValidators } from "../.."
import { FocusSet } from "../../global/globalapi"
import type {
  HookFormRenderingAfterRender_Type,
  HookFormRenderingFirstUpdated_Type,
  HookFormRenderingUpdated_Type,
  HookTableDragDrop_Type,
} from "../../global/hooks"
import {
  HookFormAutoInit,
  HookFormAutoInit_Type,
  HookFormRenderingAfterRender,
  HookFormRenderingFirstUpdated,
  HookFormRenderingUpdated,
  HookTableDragDrop,
} from "../../global/hooks"
import { DialogCurrentState } from "../dialog/dialogapi"
import { FormHandlingType } from "../dialog/sysdialogbase"
import { ScreenCurrentState } from "../screenapi"
import { SYSBaseElement, _FocusState } from "../sysbaseelement"
import type {
  ControlType,
  DragDropEventType,
  EditorType,
  SYSInputMode,
  _ColumnAlign,
} from "../table/table"
import { TableGet } from "../table/tableapi"
import type { AutoComplete, FormsState } from "./actions"
import type { ListState } from "./controls/listcontrol"
import { FormInit } from "./formapi"
import { OptionsValidateResultError, ValidatorOptions } from "./validators"

export type FieldFormat =
  | "2digits"
  | "4digits"
  | "3digits"
  | "0digits"
  | "2digitsInteger"
  | "3digitsInteger"
  | "4digitsInteger"
  | "5digitsInteger"
  | "6digitsInteger"
  | "8digitsInteger"
  | "16digitsInteger"
  | "2digitsYear"
  | "4digitsYear"
  | "timestamp"
  | "timestampWithoutSecond"

export type Schema = {
  type: DataType
  nullable: boolean
  maxLength: number
  defaultValue: any
}

export type _ClientConversion<T = any> = {
  [key in keyof T]: {
    jsonParse?: boolean
    setToNullIf?: any
    setToValueIfNull?: any
    doNotCheckForOtherModification?: boolean
  }
}

export type FieldSchema = {
  nullable?: boolean
  maxLength?: number
  defaultValue?: any
  nullValidatorToUse?: SYSFormValidationValidators
  nullValidatorOptions?: ValidatorOptions
  validator1ToUse?: SYSFormValidationValidators
  validator1Options?: ValidatorOptions
  defaultValidationOptions?: OptionsValidateResultError
  isCalculatedColumn?: boolean
  isRelevantForDirtyIfInactive?: boolean
}

export type SYSFormField = {
  control?: ControlType
  order?: number
  value?: any
  type?: DataType
  list?: ListState
  inactive?: boolean

  //removed in favor of schema.isCalculated
  //ignoreForFormValid?: boolean

  groups?: {
    tab?: number
    group?: number
  }
  nullHandling?: {
    enabled?: boolean
    setToNullIf?: any
    setToValueIfNull?: any
  }
  schema?: FieldSchema
  ui?: {
    labelTranslationKey?: string
    noLabel?: boolean
    labelCaption?: string
    format?: FieldFormat
    align?: _ColumnAlign
    option?: { inline?: boolean }
    isPassword?: boolean
    readonly?: boolean
    alwaysReadOnly?: boolean
    useSpellcheck?: boolean
    autocomplete?: AutoComplete
    checkedValue?: any
    uncheckedValue?: any
    visible?: "true" | "false" | "fadeIn" | "fadeOut"
    useBrowserDatePicker?: boolean
    fillSpaceIfNotVisible?: boolean
    infoValueTranslationCode?: string
    displayValueHintWhileTyping?: boolean
    selectIfFocused?: boolean
    inputMode?: SYSInputMode
    inputDebounceMS?: number
    addIgnoreFocusClass?: boolean
  }
  editor?: EditorType
}

export type DataType =
  | "text"
  | "date"
  | "decimal"
  | "int"
  | "inttime"
  | "bool"
  | "time"
  | "asset"
  | "datetime"
  | "object"

export type LookupResultDataType = DataType | "html"

// will be taken into account when lookupdata comes from server
// (for example it helps to reduce code ala ifnull then ... on the server)
// (or it ignores nulls completely to avouid a value "null" display if there is nothing linked)
// ( usecase B&V activities, for example the OCRD Lookup used in U_LiefCode)
// export type LookupNullHandlingType = {
//   // totally ignore null keys
//   ignoreIfKeyIsNull: boolean
//   // replaces lookup values with empty string if they are null
//   replaceLookupColumnsWithEmptyIfNull?: boolean
// }
export type LookupColumnDef = {
  type: LookupResultDataType
  translationKey?: string
  order?: number
  mapToColumn?: string
  isKey?: boolean
  defaultValue?: any
  optional?: boolean
  ignoreForSearch?: boolean
  // refreshDef?: { colKey: string; isKey?: boolean }
}
export type FormShowed = {
  formType: SYSForm
  instanceId: string
}

// export type SYSFileDropEventData = {
//   dragEvent: DragEvent
//   formState: SYSFormState
//   field: SYSField
// }
let lastFormFocusSetTS = 0

export class SYSFormElement extends SYSBaseElement {
  formHandling: FormHandlingType
  //autoFormInitHookAlways: boolean
  schema: { [key: string]: Schema }
  feature: SYSFeature

  formIgnoreAutomaticFocusSet: boolean
  forms: FormsState
  dragAndDropHookFn: any
  formAfterRenderHookFn: any
  formFirstUpdatedHookFn: any
  formUpdatedHookFn: any
  constructor() {
    super()
    this.forms = this.state.ovl.forms
  }

  handleDrop = async (e) => {
    await this.handleDragDrop("drop", e)
    //this.removeDragOverClass()
  }

  handleDragOver = (e) => {
    this.handleDragDrop("dragover", e)
  }

  handleDragStart = (e) => {
    this.handleDragDrop("dragstart", e)
  }

  getColumnElement = (
    el: HTMLElement
  ): { el: HTMLElement; columnKey: string } => {
    let depth = 0
    while (!el.getAttribute("data-col")) {
      if (el.parentElement && depth < 10) {
        el = el.parentElement
      } else {
        return undefined
      }
      depth++
    }
    return { columnKey: el.getAttribute("data-col"), el }
  }

  handleDragDrop = async (eventType: DragDropEventType, e: DragEvent) => {
    if (this.dragAndDropHookFn) {
      let col = this.getColumnElement(e.target as HTMLElement)
      await this.dragAndDropHookFn(<HookTableDragDrop_Type>{
        type: eventType,
        e,
        row: this.formState.row,
        key: this.formState.tableRowKey,
        el: col ? col.el : e.target,
        formState: this.formState,
        columnKey: col ? col.columnKey : undefined,
        tableDefId: this.formState.tableDefId,
      })
    }
  }

  getFormFieldId(id: string) {
    return this.formId + id
  }

  async init(): Promise<any> {
    try {
      let formHandling = this.formHandling
      if (formHandling) {
        this.formType = formHandling.formType
        if (formHandling.formId) {
          this.formId = formHandling.formId
        }
      }
      if (!this.formType) {
        throw new Error(
          "ovl forms: formtype is mandatory for forms (set it in init-method)!"
        )
      }

      if (!this.formId) {
        this.formId = this.formType
      }
      let formTypeForAutoInitHook = this.formType
      if (
        this.actions.app?.features[formTypeForAutoInitHook]?.hooks[
          HookFormAutoInit
        ]
      ) {
        let fieldsToSet
        let formIdToSet = this.formId
        if (formHandling?.creation?.autoInitFormFields === undefined) {
          fieldsToSet = {}
        } else {
          fieldsToSet = JSON.parse(
            JSON.stringify(formHandling.creation.autoInitFormFields)
          )
        }
        let toSet = { fieldsToSet, formIdToSet }

        await this.actions.app.features[this.formType].hooks[HookFormAutoInit](<
          HookFormAutoInit_Type
        >{
          formType: this.formType,
          formId: this.formId,
          parentFieldKey: formHandling?.creation?.parentFieldKey,
          parentFormId: formHandling?.creation?.parentFormId,
          parentFormType: formHandling?.creation?.parentFormType,
          toSet,
        })
        this.formId = toSet.formIdToSet

        this.formState = await FormInit(this.formType, toSet.fieldsToSet, {
          formId: this.formId,
          parentFormType: formHandling?.creation?.parentFormType,
          parentFormId: formHandling?.creation?.parentFormId,
          parentFieldKey: formHandling?.creation?.parentFieldKey,
          forceOverwrite: formHandling?.forceOverwrite,
          _fromAutoInit: true,
        })
      } else if (formHandling?.formType) {
        if (formHandling.creation) {
          if (formHandling.creation.autoInitFormFields === undefined) {
            formHandling.creation.autoInitFormFields = {}
          }
          this.formState = await FormInit(
            this.formType,
            formHandling.creation.autoInitFormFields,
            {
              formId: formHandling.formId ? formHandling.formId : this.formId,
              parentFormType: formHandling.creation.parentFormType,
              parentFormId: formHandling.creation.parentFormId,
              parentFieldKey: formHandling.creation.parentFieldKey,
              forceOverwrite: formHandling?.forceOverwrite,
              initialFocusFieldKey: formHandling.creation.initialFocusFieldKey,
            }
          )
        } else {
          this.formState = this.state.ovl.forms[this.formType][this.formId]
        }
        this.formType = this.formState.formType
        this.formId = this.formState.formId
      } else {
        this.formState = this.state.ovl.forms[this.formType][this.formId]
        // it was maybe changed in the formIdToSet AutoInit inside FormInit
        this.formId = this.formState.formId
      }
      this.formAfterRenderHookFn =
        this.actions.app.features?.[this.formState.feature]?.hooks?.[
          HookFormRenderingAfterRender.replace(
            "{subFeature}",
            this.formState.subFeature
          )
        ]

      this.formFirstUpdatedHookFn =
        this.actions.app.features?.[this.formState.feature]?.hooks?.[
          HookFormRenderingFirstUpdated.replace(
            "{subFeature}",
            this.formState.subFeature
          )
        ]

      this.formUpdatedHookFn =
        this.actions.app.features?.[this.formState.feature]?.hooks?.[
          HookFormRenderingUpdated.replace(
            "{subFeature}",
            this.formState.subFeature
          )
        ]

      this.dragAndDropHookFn =
        this.actions.app.features?.[this.formState.feature]?.hooks?.[
          HookTableDragDrop
        ]
      if (this.dragAndDropHookFn) {
        this.addEventListener("dragstart", this.handleDragStart)
        this.addEventListener("dragover", this.handleDragOver)
        this.addEventListener("drop", this.handleDrop)
      }
    } finally {
      await super.init()
    }
    //}
  }
  async doRender() {
    if (!this.formState) {
      let forms = this.forms
      if (forms[this.formType] && forms[this.formType][this.formId]) {
        this.formState = forms[this.formType][this.formId]
      }
    }
    super.doRender()
  }

  async updated() {
    if (this.formUpdatedHookFn && this.formState) {
      await this.formUpdatedHookFn(<HookFormRenderingUpdated_Type>{
        formState: this.formState,
        comp: this,
      })
    }
  }

  async firstUpdated() {
    /* 
    sets field focuses on screens/dialogs
    if multiple forms:
     -> first editable from first rendered form wins
     -> forceField from last rendered form

     first editable doesn't only need to be set if the screen was not visited before

    */

    let focusState: _FocusState
    // check if Dialog is Open
    let currentDialog = DialogCurrentState()
    if (currentDialog.length === 0) {
      focusState = ScreenCurrentState()?.focus
    } else {
      focusState = currentDialog[0].focus
    }
    if (!focusState) {
      // if dor some reason its empty give it some defaults
      focusState = { focusSet: false, focusWhenLeft: undefined }
    }

    let ts = new Date().getTime()
    let ftu = this.formState._state._firstTimeToUpdate
    this.formState._state._firstTimeToUpdate = false

    //if it was multiple forms (edit inlen use case copy multiple,edit multiple) then only the first one should be focus set
    if (!(ts - lastFormFocusSetTS < 200 || this.formIgnoreAutomaticFocusSet)) {
      //focus setting only needs to happen the very first time or whenever there was no focus set at all
      lastFormFocusSetTS = ts
      if (ftu || !focusState.focusSet) {
        /* inline forms are handled on user action...*/
        if (
          !this.formState.firstEditableFocusSet &&
          !this.formState.forceFocusWhenVisibleFieldKey
        ) {
          // check if we need to set to the first editable field
          if (this.formState.tableDefId) {
            let features = TableGet(this.formState.tableDefId).dataDef.tableDef[
              this.formState.tableDefId
            ].features

            if (features.focusToFirstEditableField) {
              let focusEl: HTMLCollection
              focusEl = this.getElementsByClassName("ovl-focusable")
              let fields = this.formState.fields
              if (focusEl.length > 0) {
                let foundFields = Object.keys(fields).filter(
                  (f) => fields[f].id === focusEl[0].id
                )

                if (foundFields.length > 0) {
                  this.formState.firstEditableFocusSet = true
                  this.formState.forceFocusWhenVisibleFieldKey = foundFields[0]
                }
              }
            }
          }
        }
      }
    }
    if (this.formFirstUpdatedHookFn && this.formState) {
      await this.formFirstUpdatedHookFn(<HookFormRenderingFirstUpdated_Type>{
        formState: this.formState,
        comp: this,
      })
    }

    super.firstUpdated()
  }

  async afterRender() {
    if (this.formAfterRenderHookFn && this.formState) {
      await this.formAfterRenderHookFn(<HookFormRenderingAfterRender_Type>{
        formState: this.formState,
        comp: this,
      })
    }
  }
  async connectedCallback() {
    await super.connectedCallback()
  }
  async disconnectedCallback() {
    if (this.dragAndDropHookFn) {
      this.removeEventListener("dragstart", this.handleDragStart)
      this.removeEventListener("dragover", this.handleDragOver)
      this.removeEventListener("drop", this.handleDrop)
    }
    await super.disconnectedCallback()
  }
}
